import { Fragment, useState, useEffect } from 'react';
import intl from 'react-intl-universal';
import PropTypes from 'prop-types';
import { TaskTimer } from 'tasktimer';
import Grid from '@mui/material/Grid2';

import {
  Modal,
  Typography,
  Button,
  Card,
  Stack,
  IconButton,
} from '@mui/material';
import { generate4DigitsIdentifier } from 'utils/randomIdGenerator';
import pxToRem from 'assets/theme/functions/pxToRem';
import DoCloudDropzone from 'components/DropZone';
import { ThemeProvider } from 'styled-components';
import { calcBatchTotalSize } from 'utils/fileHandling';
import {
  useUploadMissionsContextController,
  addNewAttachmentOnExistingIssue,
  setMissionStatusContext,
  contextUpdateUploadingSpeed,
} from 'context/NewIssueContext';
import { uploadBatchAttachmentsToS3 } from 'views/New/utils/utils';
import {
  startPeriodicStatusCheckForBatchAttachments,
  checkForBatchAttachmentsCompletion,
} from './utils';
import { IssuePropTypes } from 'shared/propTypes/issueType';
import {
  MISSION_STATUS,
  IssueStatus,
} from 'shared/constants/uploadAttachments';
import { zIndexValues } from 'utils/styles';
import themeDark from 'assets/theme-dark';
import theme from 'assets/theme';
import CloseIcon from '@mui/icons-material/Close';
import logger from 'utils/logger';

const log = logger.createLogger('UploadMoreAttachmentsModal');

const propTypes = {
  issue: PropTypes.shape(IssuePropTypes),
  fetchIssue: PropTypes.func,
  isOpen: PropTypes.bool,
  handleModalClose: PropTypes.func,
  darkMode: PropTypes.bool,
};

export const ModalForUploadingMoreAttachments = ({
  issue,
  fetchIssue,
  isOpen,
  handleModalClose,
  darkMode,
}) => {
  const [controllerUploadMissions, dispatchUploadMissions] =
    useUploadMissionsContextController();
  const [structuredAttachments, setStructuredAttachments] = useState([]);
  // The existing structured attachments from the issue
  const [existingStructuredAttachments, setExistingStructuredAttachments] =
    useState([]);

  useEffect(() => {
    const structureOwnedBySrcOrg = issue.attachmentsOwnedBySrcOrg.map(
      (attachment) => attachment.structureJson
    );
    const structureOwnedByDstOrg = issue.attachmentsOwnedByDstOrg.map(
      (attachment) => attachment.structureJson
    );
    const mergedStructures = [
      ...structureOwnedBySrcOrg,
      ...structureOwnedByDstOrg,
    ];
    setExistingStructuredAttachments(mergedStructures);
  }, [issue.attachments]);

  useEffect(() => {
    const id = controllerUploadMissions.newAttachmentsMissionId;
    const status =
      id in controllerUploadMissions.missions
        ? controllerUploadMissions.missions[id].status
        : '';
    if (status === MISSION_STATUS.INIT) {
      const mission = controllerUploadMissions.missions[id];
      uploadNewAttachment(mission);
    }
  }, [controllerUploadMissions.newAttachmentsMissionId]);

  const handleSendAttachmentsButtonClick = async () => {
    const batchTotalBytes = calcBatchTotalSize(structuredAttachments);

    addNewAttachmentOnExistingIssue(dispatchUploadMissions, {
      missionId: `${issue.issueIdentifierId}_${generate4DigitsIdentifier()}`,
      issueId: issue.id,
      issueIdentifierId: issue.issueIdentifierId,
      issueCreatedAt: issue.createdAt,
      structuredAttachments: structuredAttachments,
      batchTotalBytes: batchTotalBytes,
    });
  };

  //! On upload new attachment
  const uploadNewAttachment = async (mission) => {
    handleModalClose();
    setStructuredAttachments([]);

    const {
      missionId,
      issueId,
      issueIdentifierId,
      issueCreatedAt,
      structuredAttachments,
      batchTotalBytes,
    } = mission;

    // Step 1: start a timer to track the upload speed of the upload mission that is about to start;
    const timer0 = new TaskTimer(1000); // milliseconds
    const task_uploadSpeed_checker = {
      id: 'heartbeat',
      tickInterval: 2, // number of ticks between 2 task runs;
      async callback() {
        // can also be an async function, returning a promise
        contextUpdateUploadingSpeed(dispatchUploadMissions, { missionId });
        if (
          ![MISSION_STATUS.INIT, MISSION_STATUS.UPLOADING].includes(
            mission.status
          )
        ) {
          timer0.stop();
        }
      },
    };
    timer0.add(task_uploadSpeed_checker).start();

    // Step 2: Upload the attachments to S3 bucket, and notify the backend to process it;
    let attachments = [];

    try {
      setMissionStatusContext(dispatchUploadMissions, {
        missionId,
        value: MISSION_STATUS.UPLOADING,
      });
      attachments = await uploadBatchAttachmentsToS3({
        missionId,
        issueId,
        issueIdentifierId,
        issueCreatedAt,
        structuredAttachments,
        batchTotalBytes,
        srcOrgId: issue.srcOrgId,
        dstOrgId: issue.dstOrgId,
        controllerUploadMissions,
        dispatchUploadMissions,
      });
    } catch (error) {
      // if cancelled, error = {message: 'canceled', name: 'CanceledError', code: 'ERR_CANCELED'}
      if (error.code === MISSION_STATUS.ERR_CANCELED) {
        log.warn(`Upload is aborted: ${error}`);
        setMissionStatusContext(dispatchUploadMissions, {
          missionId,
          value: MISSION_STATUS.UPLOAD_ABORTED,
        });
      } else {
        log.error(`Failed to upload batch of attachments: ${error}`);
        setMissionStatusContext(dispatchUploadMissions, {
          missionId,
          value: MISSION_STATUS.UPLOAD_FAILED,
        });
      }
      return false;
    }

    setMissionStatusContext(dispatchUploadMissions, {
      missionId,
      value: MISSION_STATUS.PROCESSING,
    });

    // Step 3: Notify the backend to start a periodic check on this batch of additional attachments;
    const attachmentIds = attachments.map((item) => item.attachmentId);

    await startPeriodicStatusCheckForBatchAttachments(attachmentIds);

    // Step 4: while waiting for backend to process, poll every few seconds to check for new status;
    const timer = new TaskTimer(1000); // milliseconds
    const task1 = {
      id: 'heartbeat',
      tickInterval: 5, // number of ticks between 2 taskTuns; e.g. run every 5 sec;
      totalRuns: 1440, // times to run; max 2 hour before timeout;
      async callback() {
        // can also be an async function, returning a promise
        const issueStatusRes = await checkForBatchAttachmentsCompletion(
          issue.id,
          attachmentIds
        );
        if (issueStatusRes === IssueStatus.COMPLETE) {
          setMissionStatusContext(dispatchUploadMissions, {
            missionId,
            value: MISSION_STATUS.PROCESS_COMPLETE,
          });
          timer.stop();
          //
          await fetchIssue();
        }
        if (issueStatusRes === IssueStatus.FAILED) {
          setMissionStatusContext(dispatchUploadMissions, {
            missionId,
            value: MISSION_STATUS.PROCESS_FAILED,
          });
          timer.stop();
        }
      },
    };
    timer.add(task1).start();
  };

  return (
    <Fragment>
      <ThemeProvider theme={darkMode ? themeDark : theme}>
        <Modal
          open={isOpen}
          sx={{
            zIndex: zIndexValues.modal4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClose={(_, reason) => {
            if (reason === 'backdropClick') {
              return false;
            }
            handleModalClose();
          }}>
          <Card
            sx={{
              alignItems: 'center',
              justifyContent: 'center',
              overflowY: 'auto',
              width: '70vw',
              maxHeight: '90vh',
            }}>
            <Stack spacing={2} padding={5} sx={{ width: '100%' }}>
              <Grid container justifyContent="space-between">
                <Grid>
                  <Typography variant="h4">
                    {intl.get('issue_details_add_more_attachment_files')}
                  </Typography>
                </Grid>
                <Grid>
                  <IconButton
                    onClick={() => {
                      handleModalClose();
                      setStructuredAttachments([]);
                    }}
                    aria-label="close">
                    <CloseIcon fontSize="medium" />
                  </IconButton>
                </Grid>
              </Grid>
              <Typography>
                {intl.get('issue_details_use_drag_and_drop')}
              </Typography>

              <Stack spacing={3}>
                <div style={{ position: 'relative' }}>
                  <DoCloudDropzone
                    existingStructuredAttachments={
                      existingStructuredAttachments
                    }
                    structuredAttachments={structuredAttachments}
                    setStructuredAttachments={setStructuredAttachments}
                    darkMode={darkMode}
                    isModal={true}
                  />
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    paddingTop: '10px',
                  }}>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={structuredAttachments.length === 0}
                    style={{
                      color: '#FFF',
                      fontSize: pxToRem(18),
                    }}
                    onClick={handleSendAttachmentsButtonClick}>
                    {intl.get('issue_details_send')}
                  </Button>
                </div>
              </Stack>
            </Stack>
          </Card>
        </Modal>
      </ThemeProvider>
    </Fragment>
  );
};

ModalForUploadingMoreAttachments.propTypes = propTypes;
